import { useState, useEffect, createContext, useContext } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('admin_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setLoading(false);
    }
  }, []);

  const verifyToken = async () => {
    try {
      const response = await axios.get(`${API}/admin/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    const response = await axios.post(`${API}/admin/login`, { username, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('admin_token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('admin_token');
    setToken(null);
    setUser(null);
  };

  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${token}` }
  });

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading, getAuthHeader }}>
      {children}
    </AuthContext.Provider>
  );
};

// API Service
export const apiService = {
  // Dashboard
  getDashboardStats: (token) => 
    axios.get(`${API}/admin/dashboard/stats`, { headers: { Authorization: `Bearer ${token}` } }),
  getRecentEnquiries: (token, limit = 5) => 
    axios.get(`${API}/admin/dashboard/recent-enquiries?limit=${limit}`, { headers: { Authorization: `Bearer ${token}` } }),
  getRecentActivity: (token, limit = 10) => 
    axios.get(`${API}/admin/dashboard/recent-activity?limit=${limit}`, { headers: { Authorization: `Bearer ${token}` } }),

  // Services
  getServices: (token) => axios.get(`${API}/admin/services`, { headers: { Authorization: `Bearer ${token}` } }),
  createService: (token, data) => axios.post(`${API}/admin/services`, data, { headers: { Authorization: `Bearer ${token}` } }),
  updateService: (token, id, data) => axios.put(`${API}/admin/services/${id}`, data, { headers: { Authorization: `Bearer ${token}` } }),
  deleteService: (token, id) => axios.delete(`${API}/admin/services/${id}`, { headers: { Authorization: `Bearer ${token}` } }),

  // Products
  getProducts: (token) => axios.get(`${API}/admin/products`, { headers: { Authorization: `Bearer ${token}` } }),
  createProduct: (token, data) => axios.post(`${API}/admin/products`, data, { headers: { Authorization: `Bearer ${token}` } }),
  updateProduct: (token, id, data) => axios.put(`${API}/admin/products/${id}`, data, { headers: { Authorization: `Bearer ${token}` } }),
  deleteProduct: (token, id) => axios.delete(`${API}/admin/products/${id}`, { headers: { Authorization: `Bearer ${token}` } }),

  // Projects
  getProjects: (token) => axios.get(`${API}/admin/projects`, { headers: { Authorization: `Bearer ${token}` } }),
  createProject: (token, data) => axios.post(`${API}/admin/projects`, data, { headers: { Authorization: `Bearer ${token}` } }),
  updateProject: (token, id, data) => axios.put(`${API}/admin/projects/${id}`, data, { headers: { Authorization: `Bearer ${token}` } }),
  deleteProject: (token, id) => axios.delete(`${API}/admin/projects/${id}`, { headers: { Authorization: `Bearer ${token}` } }),

  // Reviews
  getReviews: (token) => axios.get(`${API}/admin/reviews`, { headers: { Authorization: `Bearer ${token}` } }),
  createReview: (token, data) => axios.post(`${API}/admin/reviews`, data, { headers: { Authorization: `Bearer ${token}` } }),
  updateReview: (token, id, data) => axios.put(`${API}/admin/reviews/${id}`, data, { headers: { Authorization: `Bearer ${token}` } }),
  deleteReview: (token, id) => axios.delete(`${API}/admin/reviews/${id}`, { headers: { Authorization: `Bearer ${token}` } }),

  // Offers
  getOffers: (token) => axios.get(`${API}/admin/offers`, { headers: { Authorization: `Bearer ${token}` } }),
  createOffer: (token, data) => axios.post(`${API}/admin/offers`, data, { headers: { Authorization: `Bearer ${token}` } }),
  updateOffer: (token, id, data) => axios.put(`${API}/admin/offers/${id}`, data, { headers: { Authorization: `Bearer ${token}` } }),
  deleteOffer: (token, id) => axios.delete(`${API}/admin/offers/${id}`, { headers: { Authorization: `Bearer ${token}` } }),

  // Enquiries
  getEnquiries: (token, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return axios.get(`${API}/admin/enquiries?${queryString}`, { headers: { Authorization: `Bearer ${token}` } });
  },
  updateEnquiry: (token, id, data) => axios.put(`${API}/admin/enquiries/${id}`, data, { headers: { Authorization: `Bearer ${token}` } }),
  deleteEnquiry: (token, id) => axios.delete(`${API}/admin/enquiries/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
  exportEnquiries: (token) => axios.get(`${API}/admin/enquiries/export`, { headers: { Authorization: `Bearer ${token}` } }),

  // Settings
  getSetting: (token, id) => axios.get(`${API}/admin/settings/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
  updateSetting: (token, id, data) => axios.put(`${API}/admin/settings/${id}`, data, { headers: { Authorization: `Bearer ${token}` } }),

  // Categories
  getCategories: (token, type = null) => {
    const params = type ? `?type=${type}` : '';
    return axios.get(`${API}/admin/categories${params}`, { headers: { Authorization: `Bearer ${token}` } });
  },
  createCategory: (token, data) => axios.post(`${API}/admin/categories`, data, { headers: { Authorization: `Bearer ${token}` } }),
  updateCategory: (token, id, data) => axios.put(`${API}/admin/categories/${id}`, data, { headers: { Authorization: `Bearer ${token}` } }),
  deleteCategory: (token, id) => axios.delete(`${API}/admin/categories/${id}`, { headers: { Authorization: `Bearer ${token}` } }),

  // Videos/Reels
  getVideos: (token) => axios.get(`${API}/admin/videos`, { headers: { Authorization: `Bearer ${token}` } }),
  createVideo: (token, data) => axios.post(`${API}/admin/videos`, data, { headers: { Authorization: `Bearer ${token}` } }),
  updateVideo: (token, id, data) => axios.put(`${API}/admin/videos/${id}`, data, { headers: { Authorization: `Bearer ${token}` } }),
  deleteVideo: (token, id) => axios.delete(`${API}/admin/videos/${id}`, { headers: { Authorization: `Bearer ${token}` } }),

  // Users
  getUsers: (token) => axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } }),
  createUser: (token, data) => axios.post(`${API}/admin/users`, data, { headers: { Authorization: `Bearer ${token}` } }),
  updateUser: (token, id, data) => axios.put(`${API}/admin/users/${id}`, data, { headers: { Authorization: `Bearer ${token}` } }),
  deleteUser: (token, id) => axios.delete(`${API}/admin/users/${id}`, { headers: { Authorization: `Bearer ${token}` } }),

  // Activity Logs
  getActivityLogs: (token, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return axios.get(`${API}/admin/activity-logs?${queryString}`, { headers: { Authorization: `Bearer ${token}` } });
  },

  // Change Password
  changePassword: (token, data) => axios.post(`${API}/admin/change-password`, data, { headers: { Authorization: `Bearer ${token}` } }),
};

export default apiService;
